/*
 * Party drink calculator, ported 1:1 from the Bounteous WB theme
 * (src/js/components/drink-calculator.js).
 *
 * Loaded without `defer` from sections/legacy-drink-calculator.liquid, so it runs while
 * the page is still parsing and registers before the deferred Alpine core starts.
 */
(() => {
  function drinkCalculator ({ durationMin, durationMax, guestsMin, guestsMax }) {
    return {
      // State
      guestsValue: 5,
      durationValue: durationMin,
      preferences: {
        wine: {
          value: 50,
          locked: false,
          denominator: 5,
        },
        beer: {
          value: 30,
          locked: false,
          denominator: 1,
        },
        spirits: {
          value: 20,
          locked: false,
          denominator: 15,
        },
      },
      lockedInTotal: 0,
      lockedCategoryCount: 0,

      // Guest Count
      get guestCount () {
        return this.guestsValue;
      },

      set guestCount (newCount) {
        let newValue = newCount;
        if (newCount < guestsMin) {
          newValue = guestsMin;
        } else if (newCount > guestsMax) {
          newValue = guestsMax;
        } else {
          newValue = Math.round(newCount).toFixed(0);
        }

        this.guestsValue = newValue;
        this.$refs.guestCountDisplay.innerHTML = newValue;
      },

      resizeGuestCount (e) {
        this.$refs.guestCountDisplay.innerHTML = e.target.value;
      },

      // Party Duration
      get duration () {
        return this.durationValue;
      },

      set duration (newDuration) {
        if (newDuration < durationMin) {
          this.durationValue = durationMin;
        } else if (newDuration > durationMax) {
          this.durationValue = durationMax;
        } else if (newDuration % 0.5 !== 0) {
          this.durationValue = (Math.round(newDuration * 2) / 2).toFixed(1);
        } else {
          this.durationValue = newDuration;
        }
      },

      // Drink Types
      get wine () {
        return this.preferences.wine.value;
      },

      set wine (input) {
        const oldValue = this.preferences.wine.value;

        let newValue = input;
        if (newValue > 100 - this.lockedInTotal) {
          newValue = 100 - this.lockedInTotal;
        }

        const changeAmount = newValue - oldValue;

        this.adjustOtherCategories('wine', changeAmount);
        this.preferences.wine.value = newValue;
      },

      get beer () {
        return this.preferences.beer.value;
      },

      set beer (input) {
        const oldValue = this.preferences.beer.value;

        let newValue = input;
        if (newValue > 100 - this.lockedInTotal) {
          newValue = 100 - this.lockedInTotal;
        }

        const changeAmount = newValue - oldValue;

        this.adjustOtherCategories('beer', changeAmount);

        this.preferences.beer.value = newValue;
      },

      get spirits () {
        return this.preferences.spirits.value;
      },

      set spirits (input) {
        const oldValue = this.preferences.spirits.value;

        let newValue = input;
        if (newValue > 100 - this.lockedInTotal) {
          newValue = 100 - this.lockedInTotal;
        }

        const changeAmount = newValue - oldValue;

        this.adjustOtherCategories('spirits', changeAmount);

        this.preferences.spirits.value = newValue;
      },

      // Slider Logic
      adjustOtherCategories (changedCategory, changeAmount) {
        // Get a list of the unchanged categories that are available to be adjusted (i.e. not locked)
        const otherCategories = Object.keys(this.preferences).filter(
          (category) =>
            category !== changedCategory && !this.preferences[category].locked,
        );

        let totalPercentAvailable = 0;
        let otherCategoriesAllZero = true;

        // Determine how many percentage points those other categories make up in total
        otherCategories.forEach((category) => {
          totalPercentAvailable += this.preferences[category].value;

          if (this.preferences[category].value > 0) {
            otherCategoriesAllZero = false;
          }
        });

        // ADJUST OTHER CATEGORIES

        // If the changed category was only adjusted by 1 point, choose one of the other categories to compensate for that point.  If it's already at 0 and would need to decrease further, use the other category instead
        if (otherCategories.length > 1 && Math.abs(changeAmount) === 1) {
          const categoryToUpdateIndex = Math.round(Math.random());
          const backupCategoryIndex = Math.abs(categoryToUpdateIndex - 1);

          const categoryToUpdate = otherCategories[categoryToUpdateIndex];
          const backupCategory = otherCategories[backupCategoryIndex];

          if (this.preferences[categoryToUpdate].value - changeAmount >= 0) {
            this.preferences[categoryToUpdate].value =
              this.preferences[categoryToUpdate].value - changeAmount;
          } else {
            this.preferences[backupCategory].value =
              this.preferences[backupCategory].value - changeAmount;
          }
        } else {
          otherCategories.forEach((category, i) => {
            // Determine how much the current category is contributing to the total available percentage points
            const categoryWeight =
              this.preferences[category].value / totalPercentAvailable || 0;

            // If there's a locked category, the entire change amount is applied to the remaining unlocked category
            if (this.lockedCategoryCount > 0) {
              this.preferences[category].value =
                this.preferences[category].value - changeAmount;
            } else {
              // If both other categories are already at 0, split the change evenly between them, or unevenly by 1 if the change amount is an odd number
              if (otherCategoriesAllZero) {
                if (changeAmount % 2 !== 0) {
                  this.preferences[category].value =
                    i === 0
                      ? this.preferences[category].value - (changeAmount + 1) / 2
                      : this.preferences[category].value - (changeAmount - 1) / 2;
                } else {
                  this.preferences[category].value =
                    this.preferences[category].value - changeAmount / 2;
                }
              } else {
                // In all other scenarios, split the change amount between the two remaining categories, based on the weight of percentage points each one has available.
                const newValue =
                  i === 0
                    ? Math.ceil(
                      this.preferences[category].value -
                          changeAmount * categoryWeight,
                    )
                    : Math.floor(
                      this.preferences[category].value -
                          changeAmount * categoryWeight,
                    );

                // Make sure the value doesn't exceed 100
                this.preferences[category].value =
                  newValue <= 100 ? newValue : 100;
              }
            }
          });
        }
      },

      // Lock Methods
      toggleLockPreference (category) {
        this.preferences[category].locked = !this.preferences[category].locked;

        // Track the number of percentage points that are currently locked in place
        if (this.preferences[category].locked) {
          this.lockedInTotal =
            this.lockedInTotal + this.preferences[category].value;
        } else {
          this.lockedInTotal =
            this.lockedInTotal - this.preferences[category].value;
        }

        // Track the number of categories that are currently locked
        this.lockedCategoryCount = 0;
        Object.keys(this.preferences).forEach((category) => {
          if (this.preferences[category].locked) {
            this.lockedCategoryCount += 1;
          }
        });

        // If 2 of the 3 categories are locked, or the locked category is at 100%, lock all categories
        // If all 3 categories are locked, and one categories gets unlocked, unlock all categories
        if (
          (this.preferences[category].locked && this.lockedCategoryCount > 1) ||
          (this.preferences[category].value === 100 && this.preferences[category].locked)
        ) {
          this.lockAllCategories();
        } else if (
          !this.preferences[category].locked &&
          this.lockedCategoryCount < 3
        ) {
          this.unlockAllCategories();
        }
      },

      lockAllCategories () {
        Object.keys(this.preferences).forEach((category) => {
          this.preferences[category].locked = true;
        });

        this.lockedCategoryCount = 3;
      },

      unlockAllCategories () {
        Object.keys(this.preferences).forEach((category) => {
          this.preferences[category].locked = false;
        });

        this.lockedInTotal = 0;
        this.lockedCategoryCount = 0;
      },

      // Results
      get drinks () {
        const drinksPerHour = this.duration + 1;

        const drinks = {};

        Object.keys(this.preferences).forEach((category) => {
          const percent = this.preferences[category].value / 100;

          drinks[category] = Math.ceil(
            (this.guestCount * drinksPerHour * percent) /
              this.preferences[category].denominator,
          );
        });

        return drinks;
      },
    };
  }

  const register = () => window.Alpine.data('drinkCalculator', drinkCalculator);

  if (window.Alpine) {
    register();
  } else {
    document.addEventListener('alpine:init', register);
  }
})();
